// Generic line/area chart used by the super-admin engine pages.
// Built on top of react-apexcharts (already a project dep) so it matches
// the styling of the other monitor charts. Accepts a `series` array of
// `{ label, color, data: [{ ts, v }, ...] }` and renders one line per entry.
// Null `v` values are kept as gaps (Apex handles them natively).

import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import Chart from "react-apexcharts";
import { TOKENS } from "../design/tokens";
import { formatDate } from "../../../../../utils/format";
import { withApexChartFont } from "../../../../../utils/apexChartDefaults";

const fmtTick = (ts, longRange) => {
  if (ts == null) return "";
  const d = new Date(ts);
  if (longRange) {
    return `${(d.getMonth() + 1).toString().padStart(2, "0")}/${d
      .getDate()
      .toString()
      .padStart(2, "0")}`;
  }
  return `${d.getHours().toString().padStart(2, "0")}:${d
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;
};

const fmtValue = (v, unit) => {
  if (v == null || Number.isNaN(v)) return "—";
  const abs = Math.abs(v);
  let pretty;
  if (abs >= 1000) pretty = v.toFixed(0);
  else if (abs >= 10) pretty = v.toFixed(1);
  else pretty = v.toFixed(2);
  return unit ? `${pretty}${unit}` : pretty;
};

const MetricChart = ({
  series = [],
  height = 200,
  unit = "",
  title,
  longRange = false,
}) => {
  const hasData = useMemo(
    () => series.some((s) => Array.isArray(s.data) && s.data.length > 0),
    [series]
  );

  const apexSeries = useMemo(
    () =>
      series.map((s) => ({
        name: s.label,
        data: (s.data || []).map((p) => ({
          x: typeof p.ts === "number" ? p.ts : new Date(p.ts).getTime(),
          y: p.v == null ? null : Number(p.v),
        })),
      })),
    [series]
  );

  const colors = useMemo(
    () => series.map((s) => s.color || TOKENS.primary),
    [series]
  );

  const options = useMemo(
    () =>
      withApexChartFont({
      chart: {
        type: "area",
        toolbar: { show: false },
        animations: { enabled: false },
        zoom: { enabled: false },
        sparkline: { enabled: false },
      },
      colors,
      stroke: { curve: "smooth", width: 2 },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 0.2,
          opacityFrom: 0.25,
          opacityTo: 0.02,
          stops: [0, 100],
        },
      },
      dataLabels: { enabled: false },
      grid: {
        borderColor: TOKENS.borderSubtle,
        strokeDashArray: 3,
        padding: { left: 8, right: 8, top: 0, bottom: 0 },
      },
      xaxis: {
        type: "datetime",
        labels: {
          style: { colors: TOKENS.fg3, fontSize: "11px" },
          formatter: (val) => fmtTick(val, longRange),
        },
        axisBorder: { color: TOKENS.borderSubtle },
        axisTicks: { color: TOKENS.borderSubtle },
      },
      yaxis: {
        labels: {
          style: { colors: TOKENS.fg3, fontSize: "11px" },
          formatter: (v) => fmtValue(v, unit),
        },
      },
      tooltip: {
        x: {
          formatter: (val) => formatDate(val) || "—",
        },
        y: { formatter: (v) => fmtValue(v, unit) },
        theme: "light",
      },
      legend: {
        show: series.length > 1,
        position: "top",
        horizontalAlign: "right",
        fontSize: "12px",
        labels: { colors: TOKENS.fg2 },
        markers: { width: 8, height: 8, radius: 4 },
        itemMargin: { horizontal: 8, vertical: 0 },
      },
      noData: {
        text: "No data for this range",
        style: { color: TOKENS.fg3, fontSize: "13px" },
      },
    }),
    [colors, longRange, unit, series.length]
  );

  return (
    <Box sx={{ width: "100%" }}>
      {title ? (
        <Typography
          sx={{
            fontSize: 12.5,
            fontWeight: 600,
            color: TOKENS.fg2,
            mb: 0.5,
          }}
        >
          {title}
        </Typography>
      ) : null}
      <Box sx={{ height, position: "relative" }}>
        {hasData ? (
          <Chart
            options={options}
            series={apexSeries}
            type="area"
            height={height}
          />
        ) : (
          <Box
            sx={{
              height,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: TOKENS.fg3,
              fontSize: 13,
              background: TOKENS.bgSunken,
              border: `1px dashed ${TOKENS.border}`,
              borderRadius: `${TOKENS.radiusXs}px`,
            }}
          >
            No data for this range
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MetricChart;
